import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { CalendarIcon, Loader2, PartyPopper, ShieldCheck, CreditCard, Smartphone, Landmark, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { createEnquiry, createPaymentOrder, verifyPayment, getPaymentStatus } from "@/lib/api";
import { loadRazorpayScript, RAZORPAY_DISPLAY_CONFIG } from "@/lib/razorpay";
import { getAvailableTiers, TIER_LABELS, formatINR, computeEstimate } from "@/lib/format";

const MIN_DEPOSIT = 1000;
const DEPOSIT_PERCENT = 20;
const BRAND_PRIMARY_HEX = "#2683ca";

export function EnquiryWidget({ tour, mobile = false }) {
  const availableTiers = useMemo(() => getAvailableTiers(tour), [tour]);
  const [activeTab, setActiveTab] = useState("quick");
  const [guestName, setGuestName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [travelDate, setTravelDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [travellerType, setTravellerType] = useState("2 Adults");
  const [customCount, setCustomCount] = useState(6);
  const [tierKey, setTierKey] = useState(availableTiers[0] || "");
  const [depositAmount, setDepositAmount] = useState(String(MIN_DEPOSIT));
  const [depositTouched, setDepositTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMode, setSuccessMode] = useState("lead"); // "lead" | "deposit-recorded" | "paid"
  const [errors, setErrors] = useState({});
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentConfigError, setPaymentConfigError] = useState(false);
  const [configRetry, setConfigRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setPaymentConfigError(false);
    getPaymentStatus().then((data) => { if (active) setPaymentConfig(data); })
      .catch(() => { if (active) setPaymentConfigError(true); });
    return () => { active = false; };
  }, [configRetry]);

  const paxCount = travellerType === "2 Adults" ? 2 : travellerType === "4 Adults" ? 4 : Number(customCount) || 2;
  const estimate = computeEstimate(tour, tierKey, paxCount);
  const isBookTab = activeTab === "book";

  // Suggest a 20% deposit once we have a price estimate, unless the guest has typed their own amount.
  useEffect(() => {
    if (!depositTouched && estimate?.total) {
      const suggested = Math.max(MIN_DEPOSIT, Math.round((estimate.total * DEPOSIT_PERCENT) / 100 / 100) * 100);
      setDepositAmount(String(suggested));
    }
  }, [estimate?.total, depositTouched]);

  function validate() {
    const e = {};
    if (!guestName.trim()) e.guestName = "Please enter your name";
    if (!contactNumber.trim() || !/^[0-9]{10}$/.test(contactNumber.trim())) e.contactNumber = "Enter a valid 10-digit phone number";
    if (!travelDate) e.travelDate = "Please select a preferred travel date";
    if (travellerType === "Custom" && (!Number.isInteger(Number(customCount)) || Number(customCount) < 1 || Number(customCount) > 100)) e.customCount = "Enter 1–100 travellers";
    if (isBookTab && estimate) {
      const dep = Number(depositAmount);
      if (!dep || dep < MIN_DEPOSIT) e.depositAmount = `Minimum deposit is ${formatINR(MIN_DEPOSIT)}`;
      if (estimate?.total && dep > estimate.total) e.depositAmount = "Deposit cannot exceed the estimated total";
    }
    setErrors(e);
    const fieldIds = { guestName: "guest-name", contactNumber: "contact-number", travelDate: "enquiry-travel-date", customCount: "enquiry-custom-count", depositAmount: "deposit-amount" };
    if (Object.keys(e).length) document.getElementById(fieldIds[Object.keys(e)[0]])?.focus();
    return Object.keys(e).length === 0;
  }

  function resetForm() {
    setGuestName("");
    setContactNumber("");
    setTravelDate(null);
    setDepositTouched(false);
  }

  async function handleQuickEnquiry() {
    await createEnquiry({
      tour_id: tour.id,
      guest_name: guestName.trim(),
      contact_number: contactNumber.trim(),
      travel_dates: dayjs(travelDate).format("YYYY-MM-DD"),
      total_travellers: travellerType === "Custom" ? `${customCount} Travellers` : travellerType,
      custom_traveller_count: travellerType === "Custom" ? Number(customCount) : null,
      selected_tier: tierKey || null,
      inquiry_type: "Quick Enquiry",
      estimated_price: estimate ? estimate.total : null,
    });
    setSuccessMode("lead");
    setSuccessOpen(true);
    toast.success("Your travel request has been submitted!");
    resetForm();
  }

  async function handleBookingPayment() {
    const orderPayload = {
      tour_id: tour.id,
      guest_name: guestName.trim(),
      contact_number: contactNumber.trim(),
      travel_dates: dayjs(travelDate).format("YYYY-MM-DD"),
      total_travellers: travellerType === "Custom" ? `${customCount} Travellers` : travellerType,
      custom_traveller_count: travellerType === "Custom" ? Number(customCount) : null,
      selected_tier: tierKey || null,
      estimated_price: estimate ? estimate.total : null,
      deposit_amount: Number(depositAmount),
    };

    const order = await createPaymentOrder(orderPayload);

    if (!order.payment_configured) {
      // Online payments are being set up - the deposit intent is recorded as a lead
      // and the team will follow up to collect payment via bank transfer / UPI.
      setSuccessMode("deposit-recorded");
      setSuccessOpen(true);
      toast.success("Booking request recorded! Our team will contact you to collect the deposit.");
      resetForm();
      setSubmitting(false);
      return;
    }

    await loadRazorpayScript();
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "Best Escape Holidays",
      description: `Deposit for ${tour.tour_title}`,
      prefill: { name: guestName.trim(), contact: contactNumber.trim() },
      theme: { color: BRAND_PRIMARY_HEX },
      config: RAZORPAY_DISPLAY_CONFIG,
      handler: async (rzpResponse) => {
        try {
          await verifyPayment({
            enquiry_id: order.enquiry_id,
            razorpay_order_id: rzpResponse.razorpay_order_id,
            razorpay_payment_id: rzpResponse.razorpay_payment_id,
            razorpay_signature: rzpResponse.razorpay_signature,
          });
          setSuccessMode("paid");
          setSuccessOpen(true);
          toast.success("Payment successful! Your booking is confirmed.");
          resetForm();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Payment verification failed. Please contact support.");
        } finally {
          setSubmitting(false);
        }
      },
      modal: {
        ondismiss: () => {
          setSubmitting(false);
          toast.info("Payment window closed. Your dates are not reserved yet.");
        },
      },
    });
    rzp.on("payment.failed", () => {
      toast.error("Payment failed. Please try again or contact us on WhatsApp.");
      setSubmitting(false);
    });
    rzp.open();
  }

  async function handleSubmit(event) {
    event?.preventDefault();
    if (submitting || !validate()) return;
    setSubmitting(true);
    try {
      if (isBookTab && estimate) {
        await handleBookingPayment();
      } else {
        await handleQuickEnquiry();
        setSubmitting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <>
      <Card data-testid="enquiry-widget" className={mobile ? "mobile-enquiry-widget rounded-none border-0 shadow-none" : "overflow-hidden border shadow-[var(--shadow-soft)]"}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid h-auto w-full grid-cols-2 rounded-none bg-slate-50 p-0">
            <TabsTrigger value="quick" data-testid="enquiry-tab-quick" className="rounded-none py-3 data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--brand-primary))]">
              Quick Enquiry
            </TabsTrigger>
            <TabsTrigger value="book" data-testid="enquiry-tab-book" className="rounded-none py-3 data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--brand-primary))]">
              Book Package
            </TabsTrigger>
          </TabsList>

          <form noValidate onSubmit={handleSubmit} data-testid="package-enquiry-form" className="space-y-4 p-5">
            {Object.keys(errors).length > 0 && <p data-testid="enquiry-error-summary" role="alert" className="rounded-lg border border-destructive/40 p-3 text-sm text-destructive">Please check the highlighted fields below.</p>}
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Starting From</p>
              <p className="font-display text-2xl font-bold text-slate-900">
                {tour.price_mode === "fixed" ? formatINR(tour.starting_price) : "Price on Request"}
                {tour.price_mode === "fixed" && <span className="ml-1 text-xs font-normal text-slate-500">/ person</span>}
              </p>
            </div>
            <Separator />

            <div className="space-y-1.5">
              <Label htmlFor="guest-name">Guest Name</Label>
              <Input id="guest-name" name="name" autoComplete="name" enterKeyHint="next" aria-invalid={!!errors.guestName} aria-describedby={errors.guestName ? "enquiry-name-error" : undefined} data-testid="enquiry-guest-name-input" placeholder="Your full name" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
              {errors.guestName && <p id="enquiry-name-error" data-testid="enquiry-name-error" className="text-xs text-destructive">{errors.guestName}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact-number">Contact Phone Number</Label>
              <Input id="contact-number" name="phone" type="tel" inputMode="tel" autoComplete="tel-national" enterKeyHint="next" aria-invalid={!!errors.contactNumber} aria-describedby={errors.contactNumber ? "enquiry-phone-error" : undefined} data-testid="enquiry-contact-number-input" placeholder="10-digit mobile number" value={contactNumber} onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, "").slice(0, 10))} />
              {errors.contactNumber && <p id="enquiry-phone-error" data-testid="enquiry-phone-error" className="text-xs text-destructive">{errors.contactNumber}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="enquiry-travel-date">Preferred Travel Date</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    type="button"
                    id="enquiry-travel-date"
                    aria-invalid={!!errors.travelDate}
                    data-testid="enquiry-date-picker"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {travelDate ? dayjs(travelDate).format("DD MMM YYYY") : "Select a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="enquiry-calendar-popover w-auto p-0" align="start" collisionPadding={16} data-testid="enquiry-calendar-popover">
                  <Calendar className="enquiry-calendar" classNames={mobile ? { caption: "relative flex min-h-11 items-center justify-center" } : undefined} mode="single" selected={travelDate} onSelect={(date) => { setTravelDate(date); if (date) setDateOpen(false); }} disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                </PopoverContent>
              </Popover>
              {errors.travelDate && <p className="text-xs text-red-500">{errors.travelDate}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Travellers</Label>
              <ToggleGroup
                type="single"
                value={travellerType}
                onValueChange={(v) => v && setTravellerType(v)}
                data-testid="enquiry-pax-toggle"
                className="grid grid-cols-3 gap-2"
              >
                <ToggleGroupItem value="2 Adults" data-testid="enquiry-pax-2-adults" className="border data-[state=on]:bg-[hsl(var(--brand-primary))] data-[state=on]:text-white">2 Adults</ToggleGroupItem>
                <ToggleGroupItem value="4 Adults" data-testid="enquiry-pax-4-adults" className="border data-[state=on]:bg-[hsl(var(--brand-primary))] data-[state=on]:text-white">4 Adults</ToggleGroupItem>
                <ToggleGroupItem value="Custom" data-testid="enquiry-pax-custom" className="border data-[state=on]:bg-[hsl(var(--brand-primary))] data-[state=on]:text-white">Custom</ToggleGroupItem>
              </ToggleGroup>
              {travellerType === "Custom" && (
                <Input
                  type="number"
                  inputMode="numeric"
                  id="enquiry-custom-count"
                  aria-label="Number of travellers"
                  min={1}
                  max={100}
                  data-testid="enquiry-custom-pax-input"
                  className="mt-2"
                  value={customCount}
                  onChange={(e) => setCustomCount(e.target.value)}
                  placeholder="Number of travellers"
                />
              )}
            </div>
            {errors.customCount && <p data-testid="enquiry-custom-count-error" className="text-xs text-destructive">{errors.customCount}</p>}

            {availableTiers.length > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor="enquiry-tier">Hotel Comfort Level</Label>
                <Select value={tierKey} onValueChange={setTierKey}>
                  <SelectTrigger id="enquiry-tier" data-testid="enquiry-tier-select">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTiers.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`enquiry-tier-option-${t}`} className="min-h-11">{TIER_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div data-testid="enquiry-price-summary" className="rounded-lg border bg-slate-50 p-3">
              {estimate ? (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-slate-500"><span>Tier</span><span className="font-medium text-slate-700">{TIER_LABELS[tierKey]}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Travellers</span><span className="font-medium text-slate-700">{paxCount}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Per Person</span><span className="font-medium text-slate-700">{formatINR(estimate.perPerson)}</span></div>
                  <Separator className="my-1.5" />
                  <div className="flex justify-between text-base"><span className="font-semibold text-slate-800">Estimated Total</span><span className="font-display font-bold text-[hsl(var(--brand-primary))]">{formatINR(estimate.total)}</span></div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">We&apos;ll quote you after you share your dates & group size. Excludes 5% GST unless stated.</p>
              )}
            </div>

            <TabsContent value="quick" className="m-0" />
            <TabsContent value="book" className="m-0 space-y-3">
              {estimate ? <>
                <Label htmlFor="deposit-amount">{paymentConfig?.payment_configured ? "Deposit to Pay Now (₹)" : "Proposed Deposit (₹)"}</Label>
                <Input id="deposit-amount" type="number" min={MIN_DEPOSIT} max={estimate.total} data-testid="enquiry-deposit-amount-input" value={depositAmount} onChange={(e) => { setDepositTouched(true); setDepositAmount(e.target.value); }} placeholder={`Minimum ${formatINR(MIN_DEPOSIT)}`} />
                {errors.depositAmount && <p data-testid="enquiry-deposit-error" className="text-xs text-destructive">{errors.depositAmount}</p>}
              </> : <p data-testid="enquiry-quote-required" className="rounded-lg bg-primary/5 p-3 text-sm text-muted-foreground">Request your personalised quote first. Our team will confirm pricing before you pay.</p>}
              <div data-testid="razorpay-payment-methods" className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-primary"><ShieldCheck className="h-4 w-4" /> Razorpay checkout</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-foreground">
                  {[{ name: "UPI", Icon: Smartphone }, { name: "Cards", Icon: CreditCard }, { name: "Net banking", Icon: Landmark }, { name: "EMI options", Icon: CalendarClock }].map(({ name, Icon }, index) => <span key={name} data-testid={`razorpay-method-${index}`} className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-primary" />{name}</span>)}
                </div>
                <p data-testid="razorpay-emi-note" className="mt-3 text-xs leading-relaxed text-muted-foreground">EMI is available only for eligible amounts, banks and customers, after account approval. No-cost EMI is not guaranteed.</p>
              </div>
              {paymentConfigError ? <div data-testid="razorpay-status-error" role="alert" className="text-xs text-muted-foreground">Couldn't check payment availability. <Button type="button" variant="link" size="sm" data-testid="razorpay-status-retry" onClick={() => setConfigRetry((count) => count + 1)}>Try again</Button></div>
                : !paymentConfig ? <p data-testid="razorpay-status-loading" className="animate-pulse rounded bg-muted p-3 text-xs">Checking payment availability…</p>
                : !paymentConfig.payment_configured ? <p data-testid="razorpay-not-configured" className="rounded-lg border p-3 text-xs leading-relaxed text-muted-foreground">Online payments are not enabled yet. Submit a booking request and our team will arrange payment. No money will be charged.</p>
                : <p data-testid="razorpay-configured" className="text-xs text-muted-foreground">{paymentConfig.mode === "test" ? "Test mode — no real money will be charged." : "Secure online payment. Your remaining balance is payable offline."}</p>}
            </TabsContent>

            <div className={mobile ? "mobile-enquiry-submit space-y-2" : "space-y-2"}>
            <Button
              type="submit"
              disabled={submitting || (isBookTab && !!estimate && (!paymentConfig || paymentConfigError))}
              data-testid="enquiry-submit-button"
              className="w-full bg-[hsl(var(--brand-accent))] py-6 text-base font-semibold text-slate-900 hover:bg-[hsl(var(--brand-accent))]/90 active:scale-95"
            >
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isBookTab ? !estimate ? "Request Booking Quote" : paymentConfig?.payment_configured ? `Pay ${formatINR(Number(depositAmount) || MIN_DEPOSIT)} Deposit` : "Submit Booking Request" : "Submit Travel Request"}
            </Button>
            <p className="text-center text-xs text-slate-400">
              {isBookTab && paymentConfig?.payment_configured && estimate ? "Pay securely through Razorpay. Booking is confirmed after payment verification." : "No payment required. Our travel expert will call you back shortly."}
            </p>
            </div>
          </form>
        </Tabs>
      </Card>

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent data-testid="enquiry-success-modal" className="text-center">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--brand-accent))]/30">
              <PartyPopper className="h-7 w-7 text-[hsl(var(--brand-primary))]" />
            </div>
            <DialogTitle className="text-center font-display text-xl">
              {successMode === "paid" ? "Booking Confirmed!" : successMode === "deposit-recorded" ? "Booking Request Received!" : "Request Received!"}
            </DialogTitle>
            <DialogDescription className="text-center">
              {successMode === "paid"
                ? `Your deposit payment was successful and your ${tour.tour_title} booking is confirmed. A confirmation has been recorded in our system - our team will reach out with the full itinerary.`
                : successMode === "deposit-recorded"
                ? `Thanks! We've recorded your booking request for ${tour.tour_title}. Our team will contact you shortly on WhatsApp/phone to collect the deposit and confirm your dates.`
                : `Thanks! Our team will contact you shortly on WhatsApp/phone to help plan your ${tour.tour_title} trip.`}
            </DialogDescription>
          </DialogHeader>
          <Button data-testid="enquiry-success-continue" onClick={() => setSuccessOpen(false)} className="mt-2 min-h-11 w-full bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary))]/90">
            Continue Browsing
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
